import { ApiCMSRequest } from "data/ApiCMS";
import { SchemaData, UseUpdateProps } from "./types";
import { useCallback } from "react";
import { JsonFormsStateProps } from "components/CRPComponents/JsonForm/JsonFormsComponent/types";
import { UPDATE_QUERY, UpdateGql } from "../queries/update";
import { formatTimeFromDate, isValidDate, parseEventDateTimeAsLocalDate } from "./dateUtils";
import { prepareInternalContent } from "./attendanceUtils";

const formatTicketValue = (value?: number | string) =>
  Number(value || 0).toLocaleString("pt-BR", { currency: "BRL", style: "currency" });

export default function useUpdate({ queryParam }: UseUpdateProps) {
  const handleSave = useCallback(
    async (state: JsonFormsStateProps<SchemaData>) => {
      const { createdAt, updatedAt, imageUrl, ...data } = state?.data;

      const dateInit = parseEventDateTimeAsLocalDate(data?.dateInit);
      const dateFinish = parseEventDateTimeAsLocalDate(data?.dateFinish, "23:59");
      if (!isValidDate(dateInit)) {
        throw {
          message: "Data de início do evento inválida",
          errors: [
            {
              instancePath: "/dateInit",
              message: "Data de início do evento inválida",
              schemaPath: "/dateInit",
              keyword: "dateInit",
              params: {},
            },
          ],
        };
      }

      if (!isValidDate(dateFinish)) {
        throw {
          message: "Data de término do evento inválida",
          errors: [
            {
              instancePath: "/dateFinish",
              message: "Data de término do evento inválida",
              schemaPath: "/dateFinish",
              keyword: "dateFinish",
              params: {},
            },
          ],
        };
      }

      if (dateFinish < dateInit) {
        throw {
          message: "A Data de final do Evento não pode ser antes que a Data de início do Evento",
          errors: [
            {
              instancePath: "/dateFinish",
              message:
                "A Data de final do Evento não pode ser antes que a Data de início do Evento",
              schemaPath: "/dateFinish",
              keyword: "dateFinish",
              params: {},
            },
          ],
        };
      }

      const publishOn = new Date(data?.publishOn);
      const expiresIn = new Date(data?.expiresIn);
      if (data?.publishOn && data?.expiresIn && expiresIn < publishOn) {
        throw {
          message: "A Data de expiração não pode ser antes que a Data de publicação",
          errors: [
            {
              instancePath: "/expiresIn",
              message: "A Data de expiração não pode ser antes que a Data de publicação",
              schemaPath: "/expiresIn",
              keyword: "expiresIn",
              params: {},
            },
          ],
        };
      }

      const financialIds = {
        eCommerceProductId: data?.eCommerceProductId ? Number(data.eCommerceProductId) : null,
        eCommerceControlSKUId: data?.eCommerceControlSKUId
          ? Number(data.eCommerceControlSKUId)
          : null,
        eCommerceSkuId: data?.eCommerceSkuId ? Number(data.eCommerceSkuId) : null,
      };

      const response = await ApiCMSRequest<UpdateGql>({
        query: UPDATE_QUERY,
        variables: {
          data: {
            id: queryParam,
            title: data.title,
            titleContrast: !!data.titleContrast,
            slug: data.slug,
            descriptionContentHTML: data.descriptionContentHTML,
            programationJSON: JSON.stringify(data.programationJSON),
            internalContentJSON: JSON.stringify(await prepareInternalContent(data.internalContentJSON)),
            registration:
              data?.ticketPrice !== undefined && data?.ticketPrice !== null
                ? formatTicketValue(data.ticketPrice)
                : "Inscrições gratuitas",
            dateInit,
            dateFinish,
            localization: data.localization,
            address: data.address,
            hourInit: formatTimeFromDate(dateInit),
            publishOn: data.publishOn,
            status: data.status,
            idMediaFileDesktop: data.idMediaFileDesktop,
            idMediaFileMobile: data.idMediaFileMobile,
            relatedEventsIds: data.relatedEventsIds,
            relatedPostsIds: data.relatedPostsIds,
            modality: data.modality,
            registrationLink: null,
            expiresIn: !data?.expiresIn ? null : data?.expiresIn,
            formId: !data?.formId ? null : data?.formId,
            saleEnabled: !!data?.eCommerceSkuId,
            ticketPrice:
              data?.ticketPrice === undefined || data?.ticketPrice === null
                ? null
                : Number(data.ticketPrice),
            ticketLimit:
              data?.ticketLimit === undefined || data?.ticketLimit === null || String(data.ticketLimit).trim() === ""
                ? null
                : Number(data.ticketLimit),
            ticketSalesEndAt: data?.ticketSalesEndAt ? data.ticketSalesEndAt : null,
            eCommerceProductId: financialIds.eCommerceProductId,
            eCommerceControlSKUId: financialIds.eCommerceControlSKUId,
            eCommerceSkuId: financialIds.eCommerceSkuId,
            moodleId: data?.moodleId ? data.moodleId : null,
            educationalUnitId: data?.educationalUnitId ? Number(data.educationalUnitId) : null,
            eventClassificationId: data?.eventClassificationId
              ? Number(data.eventClassificationId)
              : undefined,
          },
        },
      });

      if (response?.data?.errors) {
        console.log(response?.data?.errors);
        throw response.data.errors[0];
      }
      if (!response?.data?.data) {
        throw new Error(
          "A API do CMS não retornou a confirmação da atualização do evento."
        );
      }
      window.dispatchEvent(
        new CustomEvent("event-attendance-saved", {
          detail: { eventId: Number(queryParam) },
        })
      );
      return response;
    },
    [queryParam]
  );

  return {
    handleSave,
  };
}
